package com.mgacreative.hotelru

import androidx.compose.foundation.layout.*
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.ArrowBack
import androidx.compose.material.icons.filled.Menu
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import com.mgacreative.hotelru.shared.ui.designsystem.theme.HotelTheme
import com.mgacreative.hotelru.core.tenant.TenantSession
import com.mgacreative.hotelru.core.tenant.SessionState
import com.mgacreative.hotelru.shared.ui.designsystem.components.HFNavigationDrawer
import com.mgacreative.hotelru.shared.ui.designsystem.components.HelpScreen
import com.mgacreative.hotelru.core.network.SupabaseNetworkClient
import io.github.jan.supabase.auth.auth
import kotlinx.coroutines.launch
import org.koin.compose.koinInject

// Feature UI Imports
import com.mgacreative.hotelru.feature.admin.ui.AdminDashboardScreen
import com.mgacreative.hotelru.feature.admin.ui.AdminMenuManagementScreen
import com.mgacreative.hotelru.feature.admin.ui.HotelProfileScreen
import com.mgacreative.hotelru.feature.admin.ui.RoomManagementScreen
import com.mgacreative.hotelru.feature.admin.ui.PricingManagementScreen
import com.mgacreative.hotelru.feature.admin.ui.AdminSupplierManagementScreen
import com.mgacreative.hotelru.feature.admin.ui.AdminAgencyManagementScreen
import com.mgacreative.hotelru.feature.admin.ui.AgencyContractScreen
import com.mgacreative.hotelru.feature.admin.ui.AdminDepartmentManagementScreen
import com.mgacreative.hotelru.feature.admin.ui.AdminStaffManagementScreen
import com.mgacreative.hotelru.feature.admin.ui.AdminMembersScreen
import com.mgacreative.hotelru.feature.admin.ui.AdminBlacklistScreen
import com.mgacreative.hotelru.feature.hotel.ui.auth.AuthScreenContainer
import com.mgacreative.hotelru.feature.reports.ui.DashboardScreen
import com.mgacreative.hotelru.feature.room.ui.InUseRoomsScreen
import com.mgacreative.hotelru.feature.room.ui.RoomStatusScreen
import com.mgacreative.hotelru.feature.room.ui.RoomSummaryScreen
import com.mgacreative.hotelru.feature.reservation.ui.ReservationListScreen
import com.mgacreative.hotelru.feature.reservation.ui.NewReservationCalendarScreen
import com.mgacreative.hotelru.feature.reservation.ui.create.CreateReservationScreen
import com.mgacreative.hotelru.feature.reservation.ui.CalendarScreen
import com.mgacreative.hotelru.feature.reservation.ui.checkout.CheckOutListScreen
import com.mgacreative.hotelru.feature.reservation.ui.checkin.CheckInListScreen
import com.mgacreative.hotelru.feature.housekeeping.ui.TaskListScreen
import com.mgacreative.hotelru.feature.staff.ui.StaffListScreen
import com.mgacreative.hotelru.feature.staff.ui.ShiftPlanningScreen
import com.mgacreative.hotelru.feature.finance.ui.CashRegisterScreen
import com.mgacreative.hotelru.feature.finance.ui.ProfitReportScreen
import com.mgacreative.hotelru.feature.finance.ui.CurrentAccountsScreen
import com.mgacreative.hotelru.feature.restaurant.ui.RestaurantPosScreen
import com.mgacreative.hotelru.feature.inventory.ui.InventoryScreen
import com.mgacreative.hotelru.feature.maintenance.ui.MaintenanceListScreen

@Composable
fun WebApp() {
    HotelTheme {
        val tenantSession: TenantSession = koinInject()
        val sessionState by tenantSession.sessionState.collectAsState()
        
        Surface(modifier = Modifier.fillMaxSize(), color = MaterialTheme.colorScheme.background) {
            if (sessionState.isAuthenticated) {
                AppRootComponent(sessionState, tenantSession)
            } else {
                AuthScreenContainer()
            }
        }
    }
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
private fun AppRootComponent(sessionState: SessionState, tenantSession: TenantSession) {
    val supabaseClient: SupabaseNetworkClient = koinInject()
    val scope = rememberCoroutineScope()
    val backStack = remember { mutableStateListOf("DashboardScreen") }
    val currentScreen = backStack.lastOrNull() ?: "DashboardScreen"
    
    val popBackStack: () -> Unit = {
        if (backStack.size > 1) {
            backStack.removeAt(backStack.lastIndex)
        } else if (backStack.firstOrNull() != "DashboardScreen") {
            backStack.clear()
            backStack.add("DashboardScreen")
        }
    }
    val navigateTo: (String) -> Unit = { screen -> if (backStack.lastOrNull() != screen) backStack.add(screen) }

    val screensWithCustomTopBar = listOf(
        "AdminDashboardScreen",
        "RestaurantPosScreen",
        "AdminMenuManagementScreen",
        "HotelProfileScreen",
        "AdminStaffManagementScreen",
        "AdminSupplierManagementScreen",
        "AdminDepartmentManagementScreen",
        "AgencyContractScreen",
        "InventoryScreen",
        "CashRegisterScreen",
        "ProfitReportScreen",
        "CurrentAccountsScreen",
        "HousekeepingScreen",
        "RoomSummaryScreen",
        "ShiftPlannerScreen",
        "StaffListScreen",
        "RoomStatusScreen",
        "InUseRoomsScreen",
        "CheckInListScreen",
        "ReservationCalendarScreen"
    )
    
    fun getScreenTitle(screen: String): String = when (screen) {
        "DashboardScreen" -> "Gösterge Paneli"
        "InUseRoomsScreen" -> "Dolu Odalar"
        "RoomStatusScreen" -> "Oda Durumları"
        "RoomSummaryScreen" -> "Oda Özeti"
        "AdminDashboardScreen" -> "Sistem Yönetim Paneli"
        "HotelProfileScreen" -> "Otel Profili"
        "RoomManagementScreen" -> "Oda Yönetimi"
        "AdminMenuManagementScreen" -> "Menü Yönetimi"
        "PricingManagementScreen" -> "Fiyatlandırma Yönetimi"
        "AdminSupplierManagementScreen" -> "Tedarikçi Yönetimi"
        "AdminAgencyManagementScreen" -> "Acente Yönetimi"
        "AgencyContractScreen" -> "Acente Sözleşmeleri"
        "AdminDepartmentManagementScreen" -> "Departman Yönetimi"
        "AdminStaffManagementScreen" -> "Personel Yönetimi"
        "AdminMembersScreen" -> "Üye Yönetimi"
        "AdminBlacklistScreen" -> "Kara Liste"
        "ReservationListScreen" -> "Rezervasyon Listesi"
        "NewReservationCalendarScreen" -> "Rezervasyon Takvimi"
        "CreateReservationScreen" -> "Rezervasyon Oluştur"
        "ReservationCalendarScreen" -> "Rezervasyon Takvimi"
        "CheckOutListScreen" -> "Çıkış Listesi"
        "CheckInListScreen" -> "Giriş Listesi"
        "HousekeepingScreen" -> "Kat Hizmetleri"
        "MaintenanceScreen" -> "Bakım Onarım"
        "StaffListScreen" -> "Personel Listesi"
        "ShiftPlannerScreen" -> "Vardiya Planı"
        "CashRegisterScreen" -> "Kasa & Finans"
        "ProfitReportScreen" -> "Kar & Zarar Raporu"
        "CurrentAccountsScreen" -> "Cari Hesaplar"
        "RestaurantPosScreen" -> "Restoran POS"
        "InventoryScreen" -> "Envanter"
        "HelpScreen" -> "Yardım & Destek"
        else -> screen.replace("Screen", "")
    }

    Row(modifier = Modifier.fillMaxSize()) {
        // Sabit Sol Yan Menü (Fixed Sidebar)
        Box(
            modifier = Modifier
                .width(260.dp)
                .fillMaxHeight()
        ) {
            val currentEmail = supabaseClient.client.auth.currentUserOrNull()?.email ?: ""
            HFNavigationDrawer(
                userName = currentEmail,
                userEmail = currentEmail,
                userRole = sessionState.role.name,
                userRoles = sessionState.roles,
                onLogoutClick = {
                    scope.launch {
                        try {
                            supabaseClient.client.auth.signOut()
                        } catch (_: Exception) {}
                        tenantSession.clearSession()
                    }
                },
                onNavigate = { screen ->
                    navigateTo(screen)
                }
            )
        }

        VerticalDivider(
            modifier = Modifier.fillMaxHeight(),
            color = Color(0xFFE2E8F0),
            thickness = 1.dp
        )

        // Ana İçerik Alanı (Main Content Area)
        Box(
            modifier = Modifier
                .weight(1f)
                .fillMaxHeight()
        ) {
            Scaffold(
                topBar = {
                    if (currentScreen !in screensWithCustomTopBar) {
                        CenterAlignedTopAppBar(
                            title = { Text(com.mgacreative.hotelru.shared.ui.designsystem.i18n.l10n(getScreenTitle(currentScreen)), fontWeight = FontWeight.Bold) },
                            navigationIcon = {
                                if (currentScreen != "DashboardScreen") {
                                    IconButton(onClick = popBackStack) { Icon(Icons.AutoMirrored.Filled.ArrowBack, com.mgacreative.hotelru.shared.ui.designsystem.i18n.l10n("Geri")) }
                                }
                            },
                            actions = {
                                com.mgacreative.hotelru.shared.ui.designsystem.components.LanguageSelectorRow(
                                    modifier = Modifier.padding(end = 16.dp)
                                )
                            },
                            colors = TopAppBarDefaults.centerAlignedTopAppBarColors(
                                containerColor = Color.White,
                                titleContentColor = Color(0xFF0F172A),
                                navigationIconContentColor = Color(0xFF0F172A)
                            )
                        )
                    }
                }
            ) { padding ->
                Box(modifier = Modifier.fillMaxSize().padding(padding)) {
                    when (currentScreen) {
                        "DashboardScreen" -> DashboardScreen(onNavigate = navigateTo)
                        "InUseRoomsScreen" -> InUseRoomsScreen(onNavigate = navigateTo)
                        "RoomStatusScreen" -> RoomStatusScreen(onNavigate = navigateTo)
                        "RoomSummaryScreen" -> RoomSummaryScreen(onNavigate = navigateTo)
                        "AdminDashboardScreen" -> AdminDashboardScreen(onNavigate = navigateTo)
                        "HotelProfileScreen" -> HotelProfileScreen(onBack = popBackStack)
                        "RoomManagementScreen" -> RoomManagementScreen(onNavigateBack = popBackStack)
                        "AdminMenuManagementScreen" -> AdminMenuManagementScreen(onNavigateBack = popBackStack)
                        "PricingManagementScreen" -> PricingManagementScreen(onNavigateBack = popBackStack)
                        "AdminSupplierManagementScreen" -> AdminSupplierManagementScreen(onNavigateBack = popBackStack)
                        "AdminAgencyManagementScreen" -> AdminAgencyManagementScreen(onNavigateBack = popBackStack)
                        "AgencyContractScreen" -> AgencyContractScreen(onNavigateBack = popBackStack)
                        "AdminDepartmentManagementScreen" -> AdminDepartmentManagementScreen(onNavigateBack = popBackStack)
                        "AdminStaffManagementScreen" -> AdminStaffManagementScreen(onNavigateBack = popBackStack)
                        "AdminMembersScreen" -> AdminMembersScreen(onNavigateBack = popBackStack)
                        "AdminBlacklistScreen" -> AdminBlacklistScreen(onNavigateBack = popBackStack)
                        "ReservationListScreen" -> ReservationListScreen(onNavigate = navigateTo)
                        "NewReservationCalendarScreen" -> NewReservationCalendarScreen(onNavigate = navigateTo)
                        "CreateReservationScreen" -> CreateReservationScreen(onNavigateBack = popBackStack)
                        "ReservationCalendarScreen" -> CalendarScreen(onNavigate = navigateTo)
                        "CheckOutListScreen" -> CheckOutListScreen(onNavigate = navigateTo)
                        "CheckInListScreen" -> CheckInListScreen(onNavigate = navigateTo)
                        "HousekeepingScreen" -> TaskListScreen(onNavigateBack = popBackStack, onNavigate = navigateTo)
                        "MaintenanceScreen" -> MaintenanceListScreen(onNavigateBack = popBackStack, onNavigate = navigateTo)
                        "StaffListScreen" -> StaffListScreen(onNavigateBack = popBackStack, onNavigate = navigateTo)
                        "ShiftPlannerScreen" -> ShiftPlanningScreen(onNavigateBack = popBackStack, onNavigate = navigateTo)
                        "CashRegisterScreen" -> CashRegisterScreen(onNavigateBack = popBackStack, onNavigate = navigateTo)
                        "ProfitReportScreen" -> ProfitReportScreen(onNavigateBack = popBackStack)
                        "CurrentAccountsScreen" -> CurrentAccountsScreen(onNavigateBack = popBackStack)
                        "RestaurantPosScreen" -> RestaurantPosScreen(onNavigateBack = popBackStack)
                        "InventoryScreen" -> InventoryScreen(onNavigateBack = popBackStack)
                        "HelpScreen" -> HelpScreen()
                        else -> Box(Modifier.fillMaxSize(), contentAlignment = Alignment.Center) { Text("Ekran: $currentScreen") }
                    }
                }
            }
        }
    }
}

